import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useEffect } from "react";
import { FieldPath, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { characterAvatarValidationSchema } from "../../validations/validations";
import { INITIAL_CHARACTER_AVATAR_FORM } from "../configs";
import {
    CharacterAvatarMode,
    IApiCharacterAvatar,
    ICharacterAvatarFormData,
} from "../types";
import { mergeKioskSettings } from "../utils/mergeKioskSettings";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";
import useUploadAssets from "./useUploadAssets";

interface UseCharacterAvatarSettingsProps {
    characterId: string;
}

export const useCharacterAvatarSettings = ({ characterId }: UseCharacterAvatarSettingsProps) => {
    const { t } = useTranslation();
    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateSettings } = useKioskSettings({
        characterId,
    });
    const { handleUploadFile, isUploading } = useUploadAssets();

    // ======================= Form Setup =======================
    const methods = useForm<ICharacterAvatarFormData>({
        defaultValues: INITIAL_CHARACTER_AVATAR_FORM,
        resolver: yupResolver(characterAvatarValidationSchema()),
        mode: "onChange",
    });

    const { control, handleSubmit, formState, reset, setValue, watch } = methods;

    const currentMode = watch("mode");

    // ======================= Data Transformation =======================
    const transformAPItoForm = useCallback((apiData: IApiCharacterAvatar | undefined): ICharacterAvatarFormData => {
        if (!apiData) {
            return INITIAL_CHARACTER_AVATAR_FORM;
        }

        return {
            mode: apiData.mode || "3d",
            avatar_2d: {
                idle_video_url: apiData.avatar_2d?.idle_video_url || "",
                talking_video_url: apiData.avatar_2d?.talking_video_url || "",
                idleVideoFile: null,
                talkingVideoFile: null,
            },
            avatar_3d: {
                name: apiData.avatar_3d?.name || "",
                url: apiData.avatar_3d?.url || "",
                vrmFile: null,
            },
        };
    }, []);

    const transformFormToAPI = useCallback((formData: ICharacterAvatarFormData): IApiCharacterAvatar => {
        const now = new Date().toISOString();

        return {
            mode: formData.mode,
            avatar_2d: {
                idle_video_url: formData.avatar_2d.idle_video_url,
                talking_video_url: formData.avatar_2d.talking_video_url,
                updated_at: now,
            },
            avatar_3d: {
                name: formData.avatar_3d.name,
                url: formData.avatar_3d.url,
                updated_at: now,
            },
        };
    }, []);

    // ======================= File Handlers =======================
    const handleModeChange = useCallback(
        (mode: CharacterAvatarMode) => {
            setValue("mode", mode, { shouldDirty: true });
        },
        [setValue]
    );

    // Single cast to support dynamic field paths with mixed value types (File | null | string)
    const setField = setValue as (
        name: FieldPath<ICharacterAvatarFormData>,
        value: File | null | string,
        options?: { shouldDirty?: boolean }
    ) => void;

    const createFileSelectHandler = useCallback(
        (filePath: FieldPath<ICharacterAvatarFormData>, urlPath: FieldPath<ICharacterAvatarFormData>) =>
            (file: File | null) => {
                if (file) {
                    setField(filePath, file, { shouldDirty: true });
                } else {
                    setField(filePath, null, { shouldDirty: true });
                    setField(urlPath, "", { shouldDirty: true });
                }
            },
        [setField]
    );

    const handleVrmFileSelect = createFileSelectHandler("avatar_3d.vrmFile", "avatar_3d.url");
    const handleIdleVideoSelect = createFileSelectHandler("avatar_2d.idleVideoFile", "avatar_2d.idle_video_url");
    const handleTalkingVideoSelect = createFileSelectHandler("avatar_2d.talkingVideoFile", "avatar_2d.talking_video_url");

    // ======================= Upload Helpers =======================
    const uploadSingleFile = useCallback(
        async (file: File | null, prefix: "video" | "vrm"): Promise<string | null> => {
            if (!file) return null;

            const response = await handleUploadFile({ file, prefix });
            if (response?.url) {
                return response.url;
            }
            throw new Error(t("COMMON.UPLOAD_FAILED") || "上傳失敗");
        },
        [handleUploadFile, t]
    );

    // ======================= Submit Handler =======================
    const onSubmit = useCallback(
        async (data: ICharacterAvatarFormData) => {
            try {
                // Upload pending VRM file
                if (data.avatar_3d.vrmFile) {
                    const url = await uploadSingleFile(data.avatar_3d.vrmFile, "vrm");
                    if (url) {
                        data.avatar_3d.url = url;
                        data.avatar_3d.vrmFile = null;
                        setValue("avatar_3d.url", url);
                        setValue("avatar_3d.vrmFile", null);
                    }
                }

                // Upload pending idle video
                if (data.avatar_2d.idleVideoFile) {
                    const url = await uploadSingleFile(data.avatar_2d.idleVideoFile, "video");
                    if (url) {
                        data.avatar_2d.idle_video_url = url;
                        data.avatar_2d.idleVideoFile = null;
                        setValue("avatar_2d.idle_video_url", url);
                        setValue("avatar_2d.idleVideoFile", null);
                    }
                }

                // Upload pending talking video
                if (data.avatar_2d.talkingVideoFile) {
                    const url = await uploadSingleFile(data.avatar_2d.talkingVideoFile, "video");
                    if (url) {
                        data.avatar_2d.talking_video_url = url;
                        data.avatar_2d.talkingVideoFile = null;
                        setValue("avatar_2d.talking_video_url", url);
                        setValue("avatar_2d.talkingVideoFile", null);
                    }
                }

                const apiPayload = transformFormToAPI(data);
                const currentNative = kioskSetting?.data?.native;
                const mergedNative = mergeKioskSettings(currentNative, { character_avatar: apiPayload });

                await updateSettings({ native: mergedNative });

                reset(data);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
            }
        },
        [uploadSingleFile, transformFormToAPI, kioskSetting, updateSettings, setValue, reset, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("characterAvatar", formState.isDirty, saveHandler);

    // ======================= Initialize Form from API =======================
    useEffect(() => {
        if (!isLoadingKioskSetting) {
            const avatarData = kioskSetting?.data?.native?.character_avatar;
            const formData = transformAPItoForm(avatarData);
            reset(formData);
        }
    }, [kioskSetting?.data?.native?.character_avatar, isLoadingKioskSetting, reset, transformAPItoForm]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        formState,
        control,

        // State
        currentMode,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,

        // Handlers
        handleModeChange,
        handleVrmFileSelect,
        handleIdleVideoSelect,
        handleTalkingVideoSelect,
    };
};
